import { useEffect, useState } from 'react';
import type { FC } from 'react';

import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { authApi } from '@/api';
import IconClose from '@/assets/icons/icon-close.svg?react';
import IconEdit from '@/assets/icons/icon-edit.svg?react';
import IconSave from '@/assets/icons/icon-save.svg?react';
import Card from '@/components/RCard/index';
import Mask from '@/components/RMask';
import RNotificationCard from '@/components/RNotificationCard';
import PageHeader from '@/components/RPageHeader';
import Redirector from '@/components/RRedirector';
import { CURRENCY_SYMBOLS } from '@/contexts/currencyConstants';
import useCurrency from '@/contexts/useCurrency';
import useAuth from '@/hooks/useAuth';
import paths from '@/routes/paths';
import CookieService from '@/services/CookieService';
import LocalStorageService from '@/services/LocalStorageService';
import { handleError } from '@/utils';

import StyledPage from './styles';

const { Success } = Redirector;

const OrganizationSettings: FC = () => {
  const { t } = useTranslation();
  const { user, revalidateUser } = useAuth();
  const { currency, setCurrency } = useCurrency();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [form_general] = Form.useForm();
  const [form_billing] = Form.useForm();
  const [disabled, setDisabled] = useState<boolean>(true);
  const [isNameEditted, setNameEditted] = useState<boolean>(false);
  const [initialName, setInitialName] = useState<string>('');
  const [isOrganizationDeleted, setOrganizationDeleted] =
    useState<boolean>(false);
  const [isSaveDisabled, setSaveDisabled] = useState<boolean>(true);
  const [domainPlaceholder, setDomainPlaceholder] = useState<string>('');
  const theme = useTheme();
  const domain = import.meta.env.MODE;
  useEffect(() => {
    switch (domain) {
      case 'local-mode':
        setDomainPlaceholder('dev.ratiosim.com');
        break;
      case 'development':
        setDomainPlaceholder('dev.ratiosim.com');
        break;
      case 'production':
        setDomainPlaceholder('app.ratiosim.com');
        break;
      case 'test':
        setDomainPlaceholder('test.ratiosim.com');
        break;
      default:
        setDomainPlaceholder('app.ratiosim.com');
        break;
    }
  }, [domain]);

  useEffect(() => {
    form_general.setFieldsValue({
      name: user?.organization.name,
      currency,
    });
    setInitialName(user?.organization.name || '');
  }, [user, currency, form_general]);

  const confirmDelete = async () => {
    try {
      if (user) {
        await authApi.deleteOrganization(user.organization.id);
        setOrganizationDeleted(true);
        await CookieService.clearCookies();
        await LocalStorageService.clearLocalStorage();
      } else {
        throw new Error(t('organization.settings.notifications.userMissing'));
      }
    } catch (error) {
      handleError(
        error,
        t('organization.settings.notifications.failedToDelete')
      );
    }
  };

  const patchOrganization = async () => {
    try {
      await form_general.validateFields();
      const formResult = form_general.getFieldsValue();

      if (user && formResult.name) {
        const body = { name: formResult.name };
        const result = await authApi.patchOrganization(
          user.organization.id,
          body
        );

        if (result) {
          notification.success({
            message: t('organization.settings.notifications.nameChanged'),
          });
          setDisabled(true);
          setNameEditted(false);
          revalidateUser(); // Assuming this refreshes user-related data
        } else {
          throw new Error(
            t('organization.settings.notifications.failedToUpdateName')
          );
        }
      } else {
        notification.error({
          message: t(
            'organization.settings.notifications.organizationNameMissing'
          ),
        });
      }
    } catch (error) {
      handleError(
        error,
        t('organization.settings.notifications.failedToUpdateName')
      );
    }
  };

  return (
    <>
      {isOrganizationDeleted ? (
        <Modal
          centered
          width={1300}
          open={isOrganizationDeleted}
          footer={null}
          closable={false}
        >
          <Redirector redirectPath={paths.login} redirectionTimeMS={5000}>
            <Success
              title={t(
                'organization.settings.notifications.organizationDeleted'
              )}
              description={t(
                'organization.settings.notifications.organizationDeletedDescription'
              )}
              showBtn={false}
            />
          </Redirector>
        </Modal>
      ) : (
        <StyledPage>
          <PageHeader
            title={t('organization.settings.title')}
            description={t('organization.settings.description')}
          />
          <Form
            form={form_general}
            layout="vertical"
            initialValues={{
              name: user?.organization?.name,
              currency: currency,
            }}
            onChange={() => setNameEditted(true)}
            onValuesChange={() => {
              const nameValue = form_general.getFieldValue('name');
              setSaveDisabled(!nameValue);
            }}
          >
            <Card
              title={t('organization.settings.generalSettings.title')}
              $paddingBody="12px 24px"
              className="mt-24"
            >
              <Flex vertical gap={40} style={{ paddingTop: '12px' }}>
                <Row
                  align="middle"
                  gutter={24}
                  wrap={false}
                  style={{ height: '65px' }}
                >
                  <Col span={12}>
                    <Row align={'middle'}>
                      <Col flex={1} className="mr-8">
                        <Form.Item
                          label={t(
                            'organization.settings.generalSettings.organizationName.label'
                          )}
                          name="name"
                          rules={[
                            {
                              required: true,
                              message: t(
                                'organization.settings.generalSettings.organizationName.requiredMessage'
                              ),
                            },
                          ]}
                        >
                          <Input className="w-100" disabled={disabled} />
                        </Form.Item>
                      </Col>
                      <Flex align="center">
                        {!disabled &&
                          isNameEditted &&
                          (!isSaveDisabled ? (
                            <Popconfirm
                              title={t(
                                'organization.settings.generalSettings.organizationName.confirmChange'
                              )}
                              onConfirm={patchOrganization}
                              okText={t('organization.settings.continue')}
                            >
                              <Tooltip
                                title={t(
                                  'organization.settings.generalSettings.organizationName.saveTooltip'
                                )}
                              >
                                <Button
                                  className="mr-8"
                                  style={{ marginTop: '10px' }}
                                  icon={
                                    <IconSave fill={theme.colors.bluePrimary} />
                                  }
                                  data-testid="save-org-name-btn"
                                />
                              </Tooltip>
                            </Popconfirm>
                          ) : (
                            <Tooltip
                              title={t(
                                'organization.settings.generalSettings.organizationName.saveWithoutNameTooltip'
                              )}
                            >
                              <Button
                                className="mr-8"
                                style={{ marginTop: '10px' }}
                                icon={
                                  <IconEdit fill={theme.colors.bluePrimary} />
                                }
                                disabled
                                data-testid="edit-org-name-btn"
                              />
                            </Tooltip>
                          ))}
                        {disabled && (
                          <Tooltip
                            title={t(
                              'organization.settings.generalSettings.organizationName.editTooltip'
                            )}
                          >
                            <Button
                              style={{ marginTop: '10px' }}
                              onClick={() => {
                                setDisabled(false);
                              }}
                              icon={
                                <IconEdit fill={theme.colors.bluePrimary} />
                              }
                              data-testid="edit-org-name-btn"
                            />
                          </Tooltip>
                        )}
                        {!disabled && (
                          <Tooltip
                            title={t(
                              'organization.settings.generalSettings.organizationName.discardTooltip'
                            )}
                          >
                            <Button
                              style={{ marginTop: '10px' }}
                              onClick={() => {
                                form_general.setFieldsValue({
                                  name: initialName,
                                });
                                setDisabled(true);
                                setNameEditted(false);
                              }}
                              icon={
                                <IconClose
                                  width={12}
                                  height={12}
                                  fill={theme.colors.bluePrimary}
                                />
                              }
                            />
                          </Tooltip>
                        )}
                      </Flex>
                    </Row>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      label={t(
                        'organization.settings.generalSettings.domain.label'
                      )}
                      name="domain"
                      tooltip={t(
                        'organization.settings.generalSettings.domain.tooltip'
                      )}
                      rules={[
                        {
                          required: false,
                          message: t(
                            'organization.settings.generalSettings.domain.requiredMessage'
                          ),
                        },
                      ]}
                    >
                      <Input
                        disabled
                        placeholder={domainPlaceholder}
                        className="w-100"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row
                  align="middle"
                  gutter={24}
                  wrap={false}
                  style={{ height: '65px' }}
                >
                  <Col span={12}>
                    <Form.Item
                      label={t(
                        'organization.settings.generalSettings.currency.label'
                      )}
                      name="currency"
                    >
                      <Select
                        className="w-100"
                        onChange={(value: 'USD' | 'EUR') => {
                          setCurrency(value);
                          form_general.setFieldsValue({ currency: value });
                          notification.success({
                            message: t(
                              'organization.settings.generalSettings.currency.saved'
                            ),
                          });
                        }}
                        options={[
                          {
                            value: 'USD',
                            label: `${t(
                              'organization.settings.generalSettings.currency.dollar'
                            )} (${CURRENCY_SYMBOLS.USD})`,
                          },
                          {
                            value: 'EUR',
                            label: `${t(
                              'organization.settings.generalSettings.currency.euro'
                            )} (${CURRENCY_SYMBOLS.EUR})`,
                          },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Flex>
            </Card>
          </Form>
          <Form form={form_billing} layout="vertical">
            <Card
              title={t('organization.settings.billingInformation.title')}
              $paddingBody="12px 24px"
              className="billing-info mt-24"
            >
              <Mask
                text={t('organization.payments.emptyState.underDevelopment')}
                $isLockShown={true}
                isButtonShown={false}
                fontSize={14}
              />
              <Flex vertical gap={40} style={{ paddingTop: '12px' }}>
                <Row
                  align="middle"
                  gutter={24}
                  wrap={false}
                  style={{ height: '65px' }}
                >
                  <Col span={12}>
                    <Form.Item
                      label={t(
                        'organization.settings.billingInformation.corporateName.label'
                      )}
                      name="corporate_name"
                      rules={[
                        {
                          required: true,
                          message: t(
                            'organization.settings.billingInformation.corporateName.requiredMessage'
                          ),
                        },
                      ]}
                    >
                      <Input disabled className="w-100" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row
                  align="middle"
                  gutter={24}
                  wrap={false}
                  style={{ height: '65px' }}
                >
                  <Col span={12}>
                    <Form.Item
                      label={t(
                        'organization.settings.billingInformation.taxAdministration.label'
                      )}
                      name="tax_administration"
                      tooltip={t(
                        'organization.settings.billingInformation.taxAdministration.tooltip'
                      )}
                      rules={[
                        {
                          required: true,
                          message: t(
                            'organization.settings.billingInformation.taxAdministration.requiredMessage'
                          ),
                        },
                      ]}
                    >
                      <Input disabled className="w-100" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label={t(
                        'organization.settings.billingInformation.taxNumber.label'
                      )}
                      name="tax_number"
                      tooltip={t(
                        'organization.settings.billingInformation.taxNumber.tooltip'
                      )}
                      rules={[
                        {
                          required: true,
                          message: t(
                            'organization.settings.billingInformation.taxNumber.requiredMessage'
                          ),
                        },
                      ]}
                    >
                      <Input disabled className="w-100" />
                    </Form.Item>
                  </Col>
                </Row>
                <Flex justify="end">
                  <Button type="primary" disabled>
                    {t('organization.settings.billingInformation.change')}
                  </Button>
                </Flex>
              </Flex>
            </Card>
          </Form>
          {user?.role.name.toLocaleLowerCase() == 'admin' && (
            <Card $paddingBody="12px 24px" className="mt-24">
              <Flex justify="space-between" align="center">
                <Flex vertical>
                  <Typography.Title level={4} className="fs-17-bold">
                    {t('organization.settings.dangerZone.title')}
                  </Typography.Title>
                  <Typography.Text className="fs-14-regular">
                    {t('organization.settings.dangerZone.description')}
                  </Typography.Text>
                </Flex>
                <Button
                  type="primary"
                  danger
                  className="btn-delete"
                  onClick={() => setModalVisible(true)}
                >
                  {t('organization.settings.dangerZone.delete')}
                </Button>
              </Flex>
            </Card>
          )}
          <RNotificationCard
            title={t('organization.settings.deleteOrganization.title')}
            message={t('organization.settings.deleteOrganization.message')}
            type="warning"
            confirmText={t(
              'organization.settings.deleteOrganization.confirmText'
            )}
            cancelText={t(
              'organization.settings.deleteOrganization.cancelText'
            )}
            open={modalVisible}
            onConfirm={confirmDelete}
            onCancel={() => setModalVisible(false)}
          />
        </StyledPage>
      )}
    </>
  );
};

export default OrganizationSettings;
